import { enAU, enUS, Locale } from 'date-fns/locale';
import { useCurrentUser } from 'hooks/auth/useCurrentUser';
import i18n from 'i18n';
import { dateFormatStore } from 'i18n/dateFormatStore';
import { dateFormatEnumToLocale, localeEnumToI18n } from 'i18n/utils';
import React, { useEffect, useMemo } from 'react';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { Talent, User } from '@libs/graphql-types';

const DATE_FNS_LOCALES: Record<string, Locale> = {
  'en-US': enUS,
  'en-AU': enAU,
};

/**
 * Syncs user locale preferences from account_settings into i18n (translations)
 * and dateFormatStore (date display). Also wraps children with a locale-aware
 * MUI LocalizationProvider for date pickers.
 *
 * Must be rendered as a descendant of both ApolloProvider and Auth0Provider.
 */
const LocaleSync = ({ children }: { children: React.ReactNode }) => {
  const { user, isTalent } = useCurrentUser();

  const accountSettings = isTalent
    ? (user as Talent | undefined)?.account_settings
    : (user as User | undefined)?.account_settings;

  const locale = localeEnumToI18n(accountSettings?.locale);
  const dateLocale = dateFormatEnumToLocale(
    accountSettings?.pref_date_format,
    locale,
  );

  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale]);

  useEffect(() => {
    dateFormatStore.set(dateLocale);
  }, [dateLocale]);

  const dateFnsLocale = useMemo(
    () => DATE_FNS_LOCALES[dateLocale] ?? enUS,
    [dateLocale],
  );

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={dateFnsLocale}
    >
      {children}
    </LocalizationProvider>
  );
};

export default LocaleSync;
